/**
 * The work-order board, React edition of ui/WorkOrders.ts's openBoard.
 *
 * Same content and the same restraint - no stars, badges or scores. What
 * React adds: the board is a live projection of the mission runner, so
 * accepting or abandoning re-renders it in place instead of the old
 * close-and-reopen dance.
 */

import { Modal } from 'antd'
import { useState } from 'react'
import type { CSSProperties } from 'react'
import { t, tOr } from '../../i18n/i18n'
import { MISSIONS, type Mission } from '../../learn/missions'
import { missionState } from '../../learn/missionState'
import { missingForMission } from '../../learn/floor'
import {
  missionBriefing,
  missionTitle,
} from '../../ui/WorkOrders'
import { useLang } from '../i18n/LanguageProvider'
import { useHall, useMissionTick } from '../state/hooks'

const TRACKS: Array<{ id: Mission['track']; key: string; fallback: string }> = [
  { id: 'room', key: 'orders.TrackRoom', fallback: 'The room' },
  { id: 'tool', key: 'orders.TrackTool', fallback: 'The tool in front of you' },
  { id: 'building', key: 'orders.TrackBuilding', fallback: 'The building' },
  { id: 'capstone', key: 'orders.TrackCapstone', fallback: 'The whole line' },
]

const trackHeading: CSSProperties = {
  fontFamily: 'var(--mono, monospace)',
  fontSize: '10.5px',
  letterSpacing: '.1em',
  textTransform: 'uppercase',
  color: 'var(--ink-muted)',
  margin: '14px 0 6px',
}

/**
 * The instructor affordance: a link that hands this order to a whole class.
 * Any browser that opens it accepts the order on load - the same arming, the
 * same evidence, no server.
 */
function ClassLinkButton({ mission }: { mission: Mission }) {
  const url = `${location.origin}${location.pathname}?order=${mission.id}`
  const [label, setLabel] = useState<string | null>(null)
  return (
    <button
      className="btn"
      title={url}
      onClick={() => {
        void (async () => {
          try {
            await navigator.clipboard.writeText(url)
            setLabel(tOr('orders.LinkCopied', 'Link copied'))
          } catch {
            // Clipboard needs a secure context; the title still shows the URL.
            setLabel(url)
          }
          setTimeout(() => setLabel(null), 1600)
        })()
      }}
    >
      {label ?? tOr('orders.ClassLink', 'Class link')}
    </button>
  )
}

function OrderCard({ mission, onClose }: { mission: Mission; onClose: () => void }) {
  const isActive = missionState.activeMission?.id === mission.id
  const isDone = missionState.isCompleted(mission.id)
  // On a floor somebody built, an order can name a station that is not
  // here. It is shown rather than hidden - what a floor cannot do is the
  // lesson of building one - but it cannot be accepted.
  const missing = missingForMission(mission)
  return (
    <div className="section" style={{ padding: '10px 12px', margin: '6px 0' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <b>{missionTitle(mission)}</b>
        {isDone && (
          <span style={{ color: '#4fae6d', fontSize: 12 }}>
            {tOr('orders.Completed', 'completed')}
          </span>
        )}
        {isActive && (
          <span style={{ color: 'var(--accent, #d8a020)', fontSize: 12 }}>
            {tOr('orders.Active', 'active')}
          </span>
        )}
        {missing.length > 0 && (
          <span style={{ color: 'var(--ink-muted)', fontSize: 12 }}>
            {t('author.NotOnFloor', { tags: missing.join(', ') })}
          </span>
        )}
      </div>
      <div
        style={{ fontSize: '12.5px', lineHeight: 1.55, color: 'var(--ink-2)', margin: '5px 0 8px' }}
      >
        {missionBriefing(mission)}
      </div>
      <div className="btn-row">
        {isActive ? (
          <button className="btn" onClick={() => missionState.abandon()}>
            {tOr('orders.Abandon', 'Abandon')}
          </button>
        ) : (
          <button
            className="btn primary"
            disabled={missing.length > 0}
            title={missing.length ? t('author.NotOnFloor', { tags: missing.join(', ') }) : undefined}
            onClick={() => {
              missionState.accept(mission)
              onClose()
            }}
          >
            {tOr(isDone ? 'orders.Again' : 'orders.Accept', isDone ? 'Run again' : 'Accept')}
          </button>
        )}
        <ClassLinkButton mission={mission} />
      </div>
    </div>
  )
}

export function WorkOrdersBoard({ onClose }: { onClose: () => void }) {
  useLang()
  useMissionTick()
  useHall()
  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      title={tOr('orders.Title', 'Work orders')}
      width="min(620px, 96vw)"
      styles={{ body: { maxHeight: '74vh', overflowY: 'auto' } }}
    >
      <div className="modal-body" style={{ marginBottom: 6 }}>
        {tOr(
          'orders.BoardNote',
          'A briefing, a building in some state, and the expectation of evidence. Accepting an ' +
            'order arms the world; everything you then do lands in the journal, and the order ' +
            'completes when the evidence is in it. No score - the debrief just shows what your ' +
            'certainty cost.',
        )}
      </div>
      {TRACKS.map((track) => (
        <div key={track.id}>
          <div style={trackHeading}>{tOr(track.key, track.fallback)}</div>
          {MISSIONS.filter((m) => m.track === track.id).map((m) => (
            <OrderCard key={m.id} mission={m} onClose={onClose} />
          ))}
        </div>
      ))}
      <div className="modal-actions">
        <button className="btn" onClick={onClose}>
          {t('panel.Close')}
        </button>
      </div>
    </Modal>
  )
}
